/**
 * 汎用 classifications テーブルレンダラ (列名動的化レベル 3、docs/105 / docs/108 AW5)。
 *
 * apps/user の `apps/user/src/features/posts/classificationTableRenderer.tsx` と同実装を
 * admin posts-moderation 詳細 modal 用に複製 (apps/* 間 deep import 禁止、ESLint boundaries)。
 * `AiOutputFormatSchema.fields[]` の object-array `itemFields[]` から列構築・セル描画を行い、
 * mode 別ハードコード列名 (#/評価/対象物/想定災害/GQL観点/コメント等) を排除する。admin が
 * fields[] で列を増減すれば詳細テーブルが追従する (old2 の DynamicClassificationsTable 相当)。
 *
 * グループ化 3 パターン (old2 踏襲):
 *   1. `<prefix>_code` + `<prefix>_name` → 1 セル (code を小さく上、name を下)
 *   2. `assessment` → ポジ/ネガ配色バッジ (assessment_level / score は別列)
 *   3. それ以外は itemField 1 つ = 1 セル
 *
 * 配色は呼び出し側から positive/negative tone を受け取り (防災=green/orange、
 * ウォーカブル=pink/blue)、assessment 値が schema.assessment.positiveLabel /
 * negativeLabel に一致した行/バッジに適用する。
 */
import type { ReactNode } from 'react';
import {
  normalizeAiOutputFormat,
  type AiOutputFieldDef,
  type AiOutputFormatSchema,
} from '@citizen-walk/shared-types';

export interface ClassificationColumn {
  key: string;
  label: string;
  render: (row: Record<string, unknown>, rowIndex: number) => ReactNode;
}

/** ポジ/ネガ配色 tone (Tailwind class)。mode 別に呼び出し側が指定する。 */
export interface AssessmentTone {
  /** assessment バッジの背景 (positive)。 */
  positiveBadge: string;
  /** assessment バッジの背景 (negative)。 */
  negativeBadge: string;
  /** 行背景 (positive)。 */
  positiveRow: string;
  /** 行背景 (negative)。 */
  negativeRow: string;
}

/**
 * schema.fields[] から classifications テーブルの列定義を構築する。
 * fields に object-array が無ければ null を返す (= 呼び出し側は固定列に fallback)。
 */
export function buildClassificationColumns(
  schema: AiOutputFormatSchema,
  tone: AssessmentTone,
): ClassificationColumn[] | null {
  const norm = normalizeAiOutputFormat(schema);
  const objArr = norm.fields?.find((f) => f.kind === 'object-array');
  if (!objArr || !objArr.itemFields || objArr.itemFields.length === 0) return null;

  const itemFields = objArr.itemFields;
  const skip = new Set<string>();
  const columns: ClassificationColumn[] = [];
  const posLabel = norm.assessment?.positiveLabel ?? '';
  const negLabel = norm.assessment?.negativeLabel ?? '';

  for (const f of itemFields) {
    if (skip.has(f.key)) continue;

    // assessment は単独列として描画 (ハードコード「評価」は廃止、label は itemField のものを使う)。
    // ポジ/ネガの色付けは維持。assessment_level と score は別列。
    if (f.key === 'assessment' && f.kind === 'text') {
      columns.push({
        key: f.key,
        label: f.label || f.key,
        render: (row) => {
          const a = String(row.assessment ?? '');
          if (!a) return <span className="text-slate-400">—</span>;
          const isPos = !!posLabel && a === posLabel;
          const isNeg = !!negLabel && a === negLabel;
          const bg = isPos ? tone.positiveBadge : isNeg ? tone.negativeBadge : 'bg-slate-500';
          const level = String(row.assessment_level ?? '');
          const score = row.score;
          const suffix = level || score !== undefined ? `${level}(${String(score ?? '')})` : '';
          return (
            <span className={`inline-block rounded px-1.5 py-0.5 font-semibold text-white ${bg}`}>
              {a}
              {suffix}
            </span>
          );
        },
      });
      continue;
    }

    // *_code とその次の *_name を 1 セルに集約 (同じ prefix の場合)
    if (f.kind === 'text' && f.key.endsWith('_code')) {
      const prefix = f.key.slice(0, -'_code'.length);
      const nameField = itemFields.find((x) => x.key === `${prefix}_name`);
      if (nameField) {
        skip.add(nameField.key);
        const codeLabel = f.label || prefix;
        const groupLabel = codeLabel.replace(/\s*コード$/, '') || prefix;
        columns.push({
          key: prefix,
          label: groupLabel,
          render: (row) => (
            <>
              <div className="font-mono text-[10px] text-slate-500">{String(row[f.key] ?? '')}</div>
              <div className="text-slate-900">{String(row[nameField.key] ?? '')}</div>
            </>
          ),
        });
        continue;
      }
    }

    // 標準描画
    columns.push({
      key: f.key,
      label: f.label || f.key,
      render: (row, rowIndex) => renderCell(f, row, rowIndex),
    });
  }

  return columns;
}

function renderCell(
  field: AiOutputFieldDef,
  row: Record<string, unknown>,
  rowIndex: number,
): ReactNode {
  const raw = row[field.key];
  switch (field.kind) {
    case 'text': {
      if (field.key === 'rank') {
        const n = Number(raw);
        return <span className="font-mono">{Number.isFinite(n) ? n : rowIndex + 1}</span>;
      }
      const s = String(raw ?? '');
      return s ? (
        <span className="text-slate-700 whitespace-pre-wrap">{s}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    }
    case 'enum-array': {
      const arr = Array.isArray(raw) ? (raw as unknown[]).map((x) => String(x)) : [];
      if (arr.length === 0) return <span className="text-slate-400">—</span>;
      return (
        <div className="flex flex-wrap gap-0.5">
          {arr.map((s) => (
            <span
              key={s}
              className="px-1 py-0.5 bg-slate-200 text-slate-700 rounded text-[10px] font-mono"
            >
              {s}
            </span>
          ))}
        </div>
      );
    }
    case 'code-list-string': {
      const s = String(raw ?? '');
      return s ? (
        <span className="font-mono text-xs">{s}</span>
      ) : (
        <span className="text-slate-400">—</span>
      );
    }
    case 'object-array':
      return <span className="text-slate-400">[object]</span>;
  }
}

/**
 * 行の背景色を assessment 値から決定 (ポジティブ / ネガティブ / それ以外なし)。old2 踏襲。
 */
export function classificationRowBgClass(
  schema: AiOutputFormatSchema,
  row: Record<string, unknown>,
  tone: AssessmentTone,
): string {
  const a = String(row.assessment ?? '');
  if (a && a === schema.assessment?.positiveLabel) return tone.positiveRow;
  if (a && a === schema.assessment?.negativeLabel) return tone.negativeRow;
  return '';
}
